import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

// iOS "swipe from the left edge to go back", in JS.
//
// The native WKWebView gesture (allowsBackForwardNavigationGestures) is
// deliberately disabled (see AppDelegate.swift) because it flashes a stale
// snapshot of the previous SPA history entry. Here we detect a swipe that
// STARTS at the very left edge and travels right, then call history.back() —
// which fires popstate, so React Router + useViewTransitionLocation run the
// same directional View Transition slide as forward navigation. No snapshot,
// no flash.
//
// Overlays (booking wizard, specialist drawer) are real history entries
// (?sheet=…&bstep=N), so a plain history.back() steps through them one entry at
// a time — no special-casing here. See utils/overlayNav.js.
//
// Native-only: on web the browser/desktop already owns back, and we don't
// want to hijack horizontal gestures there.
const EDGE_PX = 30;       // touch must start within this many px of the left edge
const COMMIT_PX = 70;     // ...and travel at least this far right to fire back

export default function useEdgeSwipeBack() {
  useEffect(() => {
    if (!Capacitor?.isNativePlatform?.()) return undefined;

    let armed = false;
    let touchId = null;
    let startX = 0;
    let startY = 0;

    const onStart = (e) => {
      // Any multi-touch (pinch-to-zoom, e.g. on the map) is never a back-swipe.
      // Disarm so a finger that armed at the edge can't fire back when the
      // pinch ends with it having drifted rightward.
      if (e.touches.length > 1) { armed = false; touchId = null; return; }
      if (armed) return; // a gesture is already being tracked
      const t = e.touches[0];
      if (!t) return;
      // Never arm over the Google map — it owns its own edge pans + pinches, so
      // a left-edge map drag/zoom must not be read as a back-swipe.
      // Also skip the salon dashboard's left slide-in sidebar/drawer and the
      // react-big-calendar surface (drag-and-drop appointments): a left-edge
      // drag there is a legitimate dashboard gesture, not a navigation back.
      // Any element can opt out by tagging itself [data-no-edge-swipe].
      if (e.target?.closest?.('.gm-style, .rbc-calendar, [data-no-edge-swipe]')) return;
      // Only arm when the gesture begins right at the screen edge — keeps
      // horizontal scrollers (chip rows, carousels), which start inset from
      // the edge, from being misread as a back swipe.
      if (t.clientX > EDGE_PX) return;
      armed = true;
      touchId = t.identifier; // track THIS finger so a 2nd touch can't hijack it
      startX = t.clientX;
      startY = t.clientY;
    };

    const onEnd = (e) => {
      if (!armed) return;
      // Measure the finger that armed the gesture, not whichever touch lifts
      // first. In multi-touch, a second finger lifting elsewhere would
      // otherwise compute a bogus dx and fire an accidental back.
      const t = Array.from(e.changedTouches).find((touch) => touch.identifier === touchId);
      if (!t) return;
      armed = false;
      touchId = null;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Clearly rightward and horizontal-dominant, past the commit distance.
      if (dx > COMMIT_PX && Math.abs(dx) > Math.abs(dy) * 2) {
        if (window.history.length > 1) window.history.back();
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);
}
